import { useState } from "react";
import type { MacroWeather } from "@macroscope/shared";

interface Props {
  weather?: MacroWeather;
}

const stateStyles: Record<MacroWeather["state"], string> = {
  clear: "border-macro-up/30 bg-macro-up/10 text-macro-up",
  fair: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  mixed: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
  overcast: "border-orange-500/30 bg-orange-500/10 text-orange-400",
  stormy: "border-macro-down/30 bg-macro-down/10 text-macro-down",
};

export function MacroWeatherChip({ weather }: Props) {
  const [expanded, setExpanded] = useState(false);
  if (!weather) return <div className="text-sm text-slate-500">Loading Macro Weather…</div>;

  return (
    <div className={`rounded-xl border p-4 ${stateStyles[weather.state]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider opacity-80">Macro Weather</div>
          <div className="mt-1 text-lg font-semibold capitalize">{weather.state}</div>
          <div className="mt-2 max-w-xl text-sm opacity-90">{weather.driver_sentence}</div>
          <div className="mt-2 text-[10px] opacity-60">
            Source: {weather.source} · {new Date(weather.updated_at).toLocaleString()}
            {weather.delayed && " · delayed"} · educational
          </div>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="rounded-lg border border-current/20 px-2 py-1 text-xs hover:bg-white/5"
        >
          {expanded ? "Hide" : "Inputs"}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-current/10 pt-3">
          <div className="mb-2 text-xs opacity-70">Weighted inputs (educational)</div>
          <div className="space-y-2">
            {weather.inputs.map((input) => (
              <div key={input.engine} className="flex items-center gap-3 text-sm">
                <span className="w-24 opacity-80">{input.engine}</span>
                <div className="h-2 flex-1 rounded-full bg-black/20">
                  <div
                    className={`h-2 rounded-full ${input.value >= 0 ? "bg-current" : "bg-current opacity-70"}`}
                    style={{
                      width: `${Math.abs(input.value)}%`,
                      marginLeft: input.value >= 0 ? "50%" : `${50 - Math.abs(input.value)}%`,
                    }}
                  />
                </div>
                <span className="w-12 text-right font-mono">{input.value.toFixed(0)}</span>
                <span className="w-10 text-right text-xs opacity-70">{(input.weight * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
